import { memo, useMemo } from 'react'
import debounce from 'lodash.debounce'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useProjectFeatureEnabled from '@/hooks/useProjectFeatureEnabled'
import { useUpdateWorkflowState } from '@/service-library/hooks/workflow-states'
import queryKeys from '@/service-library/query-keys'
import { DocumentView, WorkflowState } from '@/types/workflows'
import { showErrorSnackbar } from '@/utils/snackbars'

type DocumentViews = Record<DocumentView, string>

export const documentViews: DocumentViews = {
  validation: 'Validation',
  comparison: 'Comparison',
  'split-document': 'Split Document',
  'purchase-order': 'Purchase Order',
  approvals: 'Approvals',
}

type WorkflowStateDocumentViewsProps = {
  workflowState: WorkflowState
  detailQueryKey: QueryKey
}

function WorkflowStateDocumentViews({
  workflowState,
  detailQueryKey,
}: WorkflowStateDocumentViewsProps) {
  const { isEnabled: isPurchaseOrdersEnabled } =
    useProjectFeatureEnabled('purchase-orders')
  const { show_approvals, show_approvals_v2 } = useFeatureFlagContext()
  const isApprovalsViewEnabled = show_approvals && !show_approvals_v2

  const { updateWorkflowState } = useUpdateWorkflowState({
    sideEffectQueryKeys: [queryKeys.workflows.lists()],
    detailQueryKey,
    onError: () => {
      showErrorSnackbar(
        'Unable to update document view preferences. Please try again later.',
      )
    },
  })

  const debouncedUpdateState = useMemo(
    () =>
      debounce((updatedState) => {
        if (
          !updatedState.allowed_document_views.includes(
            updatedState.default_document_view,
          )
        ) {
          updatedState.default_document_view =
            updatedState.allowed_document_views[0]
        }
        return updateWorkflowState(updatedState)
      }, 250),
    [updateWorkflowState],
  )

  const allowedDocumentViews = useMemo(() => {
    const filtered = workflowState.allowed_document_views.filter(
      (view) =>
        (isPurchaseOrdersEnabled || view !== 'purchase-order') &&
        (isApprovalsViewEnabled || view !== 'approvals'),
    )
    return filtered.length > 0 ? filtered : (['validation'] as DocumentView[])
  }, [
    workflowState.allowed_document_views,
    isPurchaseOrdersEnabled,
    isApprovalsViewEnabled,
  ])

  const defaultDocumentView = useMemo(
    () =>
      (workflowState.default_document_view === 'purchase-order' &&
        !isPurchaseOrdersEnabled) ||
      (workflowState.default_document_view === 'approvals' &&
        !isApprovalsViewEnabled)
        ? allowedDocumentViews[0]
        : workflowState.default_document_view,
    [
      workflowState.default_document_view,
      allowedDocumentViews,
      isPurchaseOrdersEnabled,
      isApprovalsViewEnabled,
    ],
  )

  const cantUnselectOnlyOption = allowedDocumentViews.length === 1

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Document Views</Typography>
        <Typography variant="body2" component="div" color="text.secondary">
          Configures which views are available when viewing a document in this
          workflow state.
        </Typography>
      </Box>
      <FormControl sx={{ maxWidth: 400 }}>
        <InputLabel id="allowed-document-views-id">Allowed Views</InputLabel>
        <Select
          size="small"
          labelId="allowed-document-views-id"
          label="Allowed Views"
          multiple
          value={allowedDocumentViews}
          onChange={(e) => {
            debouncedUpdateState({
              ...workflowState,
              allowed_document_views: e.target
                .value as WorkflowState['allowed_document_views'],
            })
          }}
        >
          {Object.entries(documentViews).map(([value, label]) => {
            if (!isPurchaseOrdersEnabled && value === 'purchase-order') {
              return null
            }
            if (!isApprovalsViewEnabled && value === 'approvals') {
              return null
            }
            return (
              <MenuItem
                key={value}
                disabled={
                  cantUnselectOnlyOption &&
                  allowedDocumentViews.includes(
                    value as WorkflowState['default_document_view'],
                  )
                }
                value={value}
              >
                {label}
              </MenuItem>
            )
          })}
        </Select>
      </FormControl>

      <TextField
        value={defaultDocumentView}
        label="Default View"
        select
        variant="outlined"
        sx={{ maxWidth: 400, textTransform: 'capitalize' }}
        onChange={(e) => {
          updateWorkflowState({
            ...workflowState,
            default_document_view: e.target
              .value as WorkflowState['default_document_view'],
          })
        }}
      >
        {allowedDocumentViews.map((view) => (
          <MenuItem key={view} value={view}>
            {documentViews[view]}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  )
}

export default memo(WorkflowStateDocumentViews)
